import { useEffect, useState, useRef } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getClients, createClient, updateClient, deleteClient, getVerticals } from "@/lib/api";
import { Plus, Pencil, Trash2, Loader2, Building2, Globe, MapPin, Users, ExternalLink, Upload, Image, Wrench } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const indianStates = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", 
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", 
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram", 
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu", 
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Overseas"
];

const employeeRanges = [
  "1-10", "11-50", "51-200", "201-500", "501-1000", "1000+"
];

const serviceOptions = [
  "Focus Product",
  "Focus ERP",
  "Odoo ERP",
  "Custom App Development",
  "Digital Marketing Services",
  "HRMS Product",
  "Consulting Services",
  "Others"
];

const emptyClient = {
  name: "",
  logo_url: "",
  about: "",
  website: "",
  state: "",
  industry: "",
  employee_count: "",
  vertical_ids: [],
  services_provided: [],
  onboard_date: ""
};

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [formData, setFormData] = useState({ ...emptyClient });
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const getLogoSrc = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${API_URL}${url}`;
  };

  const handleLogoUpload = async (clientId, file) => {
    if (!file) return;
    setUploadingLogo(true);
    try {
      const token = localStorage.getItem("token");
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API_URL}/api/clients/${clientId}/upload-logo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) throw new Error("Upload failed");
      const data = await res.json();
      setFormData(prev => ({ ...prev, logo_url: data.logo_url }));
      toast.success("Logo uploaded!");
      fetchData();
    } catch (err) {
      toast.error("Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const fetchData = async () => {
    try {
      const [clientsRes, verticalsRes] = await Promise.all([
        getClients(),
        getVerticals()
      ]);
      setClients(clientsRes.data);
      setVerticals(verticalsRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleOpenDialog = (client = null) => {
    if (client) {
      setEditingClient(client);
      setFormData({
        name: client.name || "",
        logo_url: client.logo_url || "",
        about: client.about || "",
        website: client.website || "",
        state: client.state || "",
        industry: client.industry || "",
        employee_count: client.employee_count || "",
        vertical_ids: client.vertical_ids || [],
        services_provided: client.services_provided || [],
        onboard_date: client.onboard_date || ""
      });
    } else {
      setEditingClient(null);
      setFormData({ ...emptyClient });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name) {
      toast.error("Client name is required");
      return;
    }
    setSaving(true);
    try {
      if (editingClient) {
        await updateClient(editingClient.id, formData);
        toast.success("Client updated successfully");
      } else {
        await createClient(formData);
        toast.success("Client created successfully");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to save client");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    try {
      await deleteClient(id);
      toast.success("Client deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete client");
    }
  };

  const toggleVertical = (verticalId) => {
    const current = formData.vertical_ids || [];
    if (current.includes(verticalId)) {
      setFormData({ ...formData, vertical_ids: current.filter(id => id !== verticalId) });
    } else {
      setFormData({ ...formData, vertical_ids: [...current, verticalId] });
    }
  };

  const toggleService = (service) => {
    const current = formData.services_provided || [];
    if (current.includes(service)) {
      setFormData({ ...formData, services_provided: current.filter(s => s !== service) });
    } else {
      setFormData({ ...formData, services_provided: [...current, service] });
    }
  };

  const getVerticalNames = (verticalIds) => {
    if (!verticalIds || verticalIds.length === 0) return "—";
    return verticalIds.map(id => {
      const v = verticals.find(v => v.id === id);
      return v?.name || id;
    }).join(", ");
  };

  return (
    <AdminLayout title="Client Master">
      <div className="space-y-6" data-testid="admin-clients">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-slate-600">Manage client profiles for hover display on logos</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowBulkEdit(true)} data-testid="bulk-update-btn">
              <Pencil className="h-4 w-4 mr-2" /> Bulk Update
            </Button>
            <Button onClick={() => handleOpenDialog()} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="add-client-btn">
              <Plus className="h-4 w-4 mr-2" /> Add Client
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total Clients</p>
              <p className="text-2xl font-bold">{clients.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">With Logos</p>
              <p className="text-2xl font-bold text-blue-600">{clients.filter(c => c.logo_url).length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">With Websites</p>
              <p className="text-2xl font-bold text-emerald-600">{clients.filter(c => c.website).length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">With Profiles</p>
              <p className="text-2xl font-bold text-purple-600">{clients.filter(c => c.about).length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">Logo</TableHead>
                  <TableHead>Company Name</TableHead>
                  <TableHead className="hidden md:table-cell">Industry</TableHead>
                  <TableHead className="hidden md:table-cell">State</TableHead>
                  <TableHead className="hidden lg:table-cell">Verticals</TableHead>
                  <TableHead className="hidden lg:table-cell">Services</TableHead>
                  <TableHead className="w-24">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : clients.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      No clients found. Add your first client.
                    </TableCell>
                  </TableRow>
                ) : (
                  clients.map((client) => (
                    <TableRow key={client.id}>
                      <TableCell>
                        <div className="w-12 h-12 rounded-lg bg-slate-100 flex items-center justify-center overflow-hidden">
                          {client.logo_url ? (
                            <img 
                              src={getLogoSrc(client.logo_url)} 
                              alt={client.name}
                              className="w-full h-full object-contain p-1"
                              onError={(e) => e.target.style.display = 'none'}
                            />
                          ) : (
                            <Building2 className="h-6 w-6 text-slate-400" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{client.name}</p>
                          {client.website && (
                            <a 
                              href={client.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-xs text-[#0066CC] hover:underline flex items-center gap-1"
                            >
                              <Globe className="h-3 w-3" />
                              Website
                            </a>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {client.industry || "—"}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {client.state || "—"}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <span className="text-sm text-slate-500 truncate max-w-[200px] block">
                          {getVerticalNames(client.vertical_ids)}
                        </span>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div className="flex flex-wrap gap-1 max-w-[180px]">
                          {client.services_provided && client.services_provided.length > 0
                            ? client.services_provided.map((svc, i) => (
                                <span key={`${svc}-${i}`} className="px-1.5 py-0.5 bg-emerald-50 text-emerald-700 rounded text-[10px] font-medium">
                                  {svc}
                                </span>
                              ))
                            : <span className="text-slate-400 text-sm">—</span>
                          }
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleOpenDialog(client)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(client.id)} className="text-red-500">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingClient ? "Edit Client" : "Add New Client"}</DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
              <div className="space-y-2 md:col-span-2">
                <Label>Company Name *</Label>
                <Input 
                  value={formData.name} 
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Lalitha Super Market Chain"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Logo</Label>
                <div className="flex items-start gap-4">
                  <div className="h-20 w-24 bg-slate-100 rounded-lg flex items-center justify-center overflow-hidden border border-slate-200 flex-shrink-0">
                    {formData.logo_url ? (
                      <img src={getLogoSrc(formData.logo_url)} alt="Preview" className="max-h-full max-w-full object-contain p-1" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <Image className="h-8 w-8 text-slate-300" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    {editingClient && (
                      <div>
                        <label className="flex items-center gap-2 px-4 py-2 bg-[#0066CC] text-white rounded-lg cursor-pointer hover:bg-[#0052A3] transition-colors text-sm font-medium w-fit">
                          <Upload className="h-4 w-4" />
                          {uploadingLogo ? "Uploading..." : "Upload Logo (PNG/JPG)"}
                          <input
                            type="file"
                            accept=".png,.jpg,.jpeg"
                            className="hidden"
                            disabled={uploadingLogo}
                            onChange={(e) => handleLogoUpload(editingClient.id, e.target.files[0])}
                            data-testid="logo-upload-input"
                          />
                        </label>
                      </div>
                    )}
                    <div>
                      <Input 
                        value={formData.logo_url} 
                        onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                        placeholder="Or paste a logo URL..."
                        className="text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Website</Label>
                <Input 
                  value={formData.website} 
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="https://www.company.com"
                />
              </div>

              <div className="space-y-2">
                <Label>Industry</Label>
                <Input 
                  value={formData.industry} 
                  onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                  placeholder="e.g., Retail, Manufacturing"
                />
              </div>

              <div className="space-y-2">
                <Label>State</Label>
                <Select value={formData.state} onValueChange={(v) => setFormData({ ...formData, state: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {indianStates.map(state => (
                      <SelectItem key={state} value={state}>{state}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Employee Count</Label>
                <Select value={formData.employee_count} onValueChange={(v) => setFormData({ ...formData, employee_count: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select range" />
                  </SelectTrigger>
                  <SelectContent>
                    {employeeRanges.map(range => (
                      <SelectItem key={range} value={range}>{range}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Onboard Date</Label>
                <Input
                  type="date"
                  value={formData.onboard_date}
                  onChange={(e) => setFormData({ ...formData, onboard_date: e.target.value })}
                  data-testid="client-onboard-date"
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>About the Company</Label>
                <Textarea 
                  value={formData.about} 
                  onChange={(e) => setFormData({ ...formData, about: e.target.value })}
                  placeholder="Brief description about the company..."
                  rows={3}
                />
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Associated Verticals</Label>
                <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg">
                  {verticals.map(v => (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => toggleVertical(v.id)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        formData.vertical_ids?.includes(v.id)
                          ? 'bg-[#0066CC] text-white'
                          : 'bg-white border border-slate-200 text-slate-700 hover:border-[#0066CC]'
                      }`}
                    >
                      {v.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>Services Provided</Label>
                <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-lg" data-testid="services-provided-selector">
                  {serviceOptions.map(svc => (
                    <button
                      key={svc}
                      type="button"
                      onClick={() => toggleService(svc)}
                      className={`px-3 py-1.5 rounded-full text-sm transition-all ${
                        formData.services_provided?.includes(svc)
                          ? 'bg-emerald-600 text-white'
                          : 'bg-white border border-slate-200 text-slate-700 hover:border-emerald-500'
                      }`}
                      data-testid={`service-option-${svc.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      {svc}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3]">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingClient ? "Update Client" : "Create Client"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Update Dialog */}
        <ClientsBulkEditDialog
          open={showBulkEdit}
          onOpenChange={setShowBulkEdit}
          clients={clients}
          verticals={verticals}
          onSaved={fetchData}
        />
      </div>
    </AdminLayout>
  );
}

// ===== Clients Bulk Edit Grid =====
function ClientsBulkEditDialog({ open, onOpenChange, clients, verticals, onSaved }) {
  const [rows, setRows] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setRows(clients.map(c => ({
      id: c.id,
      name: c.name || "",
      website: c.website || "",
      vertical_ids: Array.isArray(c.vertical_ids) ? [...c.vertical_ids] : [],
      services_provided: Array.isArray(c.services_provided) ? [...c.services_provided] : [],
      state: c.state || "",
      employee_count: c.employee_count || "",
      onboard_date: c.onboard_date || "",
      _dirty: false,
    })));
  }, [open, clients]);

  const update = (id, patch) => setRows(rs => rs.map(r => r.id === id ? { ...r, ...patch, _dirty: true } : r));

  const toggleVertical = (id, vid) => {
    setRows(rs => rs.map(r => {
      if (r.id !== id) return r;
      const next = r.vertical_ids.includes(vid) ? r.vertical_ids.filter(x => x !== vid) : [...r.vertical_ids, vid];
      return { ...r, vertical_ids: next, _dirty: true };
    }));
  };
  const toggleService = (id, svc) => {
    setRows(rs => rs.map(r => {
      if (r.id !== id) return r;
      const next = r.services_provided.includes(svc) ? r.services_provided.filter(x => x !== svc) : [...r.services_provided, svc];
      return { ...r, services_provided: next, _dirty: true };
    }));
  };

  const saveRow = async (row) => {
    setSavingId(row.id);
    try {
      await updateClient(row.id, {
        name: row.name,
        website: row.website,
        vertical_ids: row.vertical_ids,
        services_provided: row.services_provided,
        state: row.state,
        employee_count: row.employee_count,
        onboard_date: row.onboard_date,
      });
      setRows(rs => rs.map(r => r.id === row.id ? { ...r, _dirty: false } : r));
      toast.success(`Saved: ${row.name}`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSavingId(null);
  };

  const filtered = rows.filter(r => !search || r.name.toLowerCase().includes(search.toLowerCase()));
  const dirtyCount = rows.filter(r => r._dirty).length;
  const saveAllDirty = async () => { for (const r of rows.filter(x => x._dirty)) await saveRow(r); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1400px] max-h-[90vh] overflow-hidden flex flex-col" data-testid="bulk-edit-clients-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Pencil className="w-5 h-5 text-[#0066CC]" /> Bulk Update Clients</DialogTitle>
          <DialogDescription>Inline-edit company name, website, associated verticals, services provided, state, employees and onboard date. Save each row individually or use Save All.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between gap-2 py-2">
          <div className="relative flex-1 max-w-sm">
            <Input className="h-9" placeholder="Search by name..." value={search} onChange={e => setSearch(e.target.value)} data-testid="clients-bulk-search" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">{dirtyCount} unsaved</span>
            <Button onClick={saveAllDirty} disabled={!dirtyCount || !!savingId} className="bg-[#0066CC] hover:bg-[#0052A3] gap-1.5 h-9" data-testid="clients-bulk-save-all">
              Save All ({dirtyCount})
            </Button>
          </div>
        </div>
        <div className="overflow-auto flex-1 border border-slate-200 rounded-lg">
          <table className="w-full text-xs" data-testid="clients-bulk-table">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr>
                <th className="text-left p-2 font-semibold text-slate-600 min-w-[200px]">Company Name</th>
                <th className="text-left p-2 font-semibold text-slate-600 min-w-[180px]">Website</th>
                <th className="text-left p-2 font-semibold text-slate-600 min-w-[260px]">Associated Verticals</th>
                <th className="text-left p-2 font-semibold text-slate-600 min-w-[260px]">Services Provided</th>
                <th className="text-left p-2 font-semibold text-slate-600 min-w-[130px]">State</th>
                <th className="text-left p-2 font-semibold text-slate-600 min-w-[110px]">Employees</th>
                <th className="text-left p-2 font-semibold text-slate-600 min-w-[130px]">Onboard Date</th>
                <th className="text-center p-2 font-semibold text-slate-600 sticky right-0 bg-slate-50 w-[70px]">Save</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} className={`border-t border-slate-100 align-top ${r._dirty ? "bg-amber-50/40" : "hover:bg-slate-50"}`}>
                  <td className="p-1.5"><Input className="h-8 text-xs" value={r.name} onChange={e => update(r.id, { name: e.target.value })} data-testid={`client-row-name-${r.id}`} /></td>
                  <td className="p-1.5"><Input className="h-8 text-xs" value={r.website} onChange={e => update(r.id, { website: e.target.value })} placeholder="https://" /></td>
                  <td className="p-1.5">
                    <MultiSelectChips
                      options={(verticals || []).map(v => ({ value: v.id, label: v.name }))}
                      values={r.vertical_ids}
                      onToggle={(vid) => toggleVertical(r.id, vid)}
                      testIdPrefix={`client-row-vert-${r.id}`}
                      placeholder="Select verticals…"
                    />
                  </td>
                  <td className="p-1.5">
                    <MultiSelectChips
                      options={serviceOptions.map(s => ({ value: s, label: s }))}
                      values={r.services_provided}
                      onToggle={(svc) => toggleService(r.id, svc)}
                      testIdPrefix={`client-row-svc-${r.id}`}
                      placeholder="Select services…"
                    />
                  </td>
                  <td className="p-1.5">
                    <select value={r.state} onChange={e => update(r.id, { state: e.target.value })} className="w-full h-8 text-xs border border-slate-200 rounded px-2">
                      <option value="">—</option>
                      {indianStates.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="p-1.5">
                    <select value={r.employee_count} onChange={e => update(r.id, { employee_count: e.target.value })} className="w-full h-8 text-xs border border-slate-200 rounded px-2">
                      <option value="">—</option>
                      {employeeRanges.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="p-1.5"><Input type="date" className="h-8 text-xs" value={r.onboard_date} onChange={e => update(r.id, { onboard_date: e.target.value })} /></td>
                  <td className="p-1.5 sticky right-0 bg-white text-center">
                    <Button size="sm" variant="ghost" disabled={!r._dirty || savingId === r.id} onClick={() => saveRow(r)} className="h-8 w-full text-xs" data-testid={`client-row-save-${r.id}`}>
                      {savingId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (r._dirty ? "Save" : "✓")}
                    </Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-slate-400 text-xs">No clients match filter.</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-slate-400 italic">Tip: yellow rows have unsaved changes. Click the row's Save button or use Save All.</p>
      </DialogContent>
    </Dialog>
  );
}

// ===== Multi-select chips popover (collapsed pill-summary + expand to choose) =====
function MultiSelectChips({ options, values, onToggle, testIdPrefix, placeholder }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const selectedLabels = options.filter(o => values.includes(o.value)).map(o => o.label);
  const summary = selectedLabels.length === 0 ? placeholder
    : selectedLabels.length <= 2 ? selectedLabels.join(", ")
    : `${selectedLabels.slice(0, 2).join(", ")} +${selectedLabels.length - 2}`;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={`w-full h-8 text-xs text-left border border-slate-200 rounded px-2 truncate hover:border-[#0066CC] transition ${selectedLabels.length === 0 ? "text-slate-400" : "text-slate-700"}`}
        data-testid={`${testIdPrefix}-trigger`}
      >
        {summary} <span className="float-right text-slate-400">▾</span>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-[280px] max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-xl p-2">
          {options.length === 0 && <p className="text-xs text-slate-400 italic p-2">No options</p>}
          {options.map(opt => {
            const checked = values.includes(opt.value);
            return (
              <label key={opt.value} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
                <input type="checkbox" checked={checked} onChange={() => onToggle(opt.value)} className="w-3.5 h-3.5 accent-[#0066CC]" data-testid={`${testIdPrefix}-opt-${opt.value}`} />
                <span className="text-xs text-slate-700">{opt.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
